import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Box, Text, IconButton, HStack, VStack } from '@chakra-ui/react'
import { motion, useReducedMotion } from 'framer-motion'

// Inline SVG icons
const PlayIcon  = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="#1A1D24" aria-hidden="true"><polygon points="6,4 20,12 6,20"/></svg>)
const PauseIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="#1A1D24" aria-hidden="true"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>)
const ReplayIcon = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F8F4EC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>)

const AudioBar = ({ description, autoPlay = true, autoPlayDelay = 350 }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress]   = useState(0)
  const [status, setStatus]       = useState('idle') // 'idle' | 'playing' | 'paused' | 'done'

  const utteranceRef            = useRef(null)
  const intervalRef             = useRef(null)
  const startTimeRef            = useRef(0)
  const durationEstimateRef     = useRef(0)
  const boundaryFiredRef        = useRef(false)
  const boundaryCheckTimeoutRef = useRef(null)
  const prefersReduced          = useReducedMotion()

  const clearInterval_ = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const clearBoundaryCheck = useCallback(() => {
    if (boundaryCheckTimeoutRef.current) {
      window.clearTimeout(boundaryCheckTimeoutRef.current)
      boundaryCheckTimeoutRef.current = null
    }
  }, [])

  const startFallbackTimer = useCallback(() => {
    const wordCount = description.trim().split(/\s+/).length
    durationEstimateRef.current = (wordCount / 150) * 60 * 1000
    clearInterval_()
    intervalRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current
      const pct = Math.min((elapsed / durationEstimateRef.current) * 100, 99)
      setProgress(pct)
    }, 100)
  }, [description, clearInterval_])

  const speak = useCallback(() => {
    if (!('speechSynthesis' in window) || !description) return
    window.speechSynthesis.cancel()
    clearInterval_()
    clearBoundaryCheck()

    const u = new window.SpeechSynthesisUtterance(description)
    u.lang  = 'fr-FR'
    u.rate  = 0.95
    boundaryFiredRef.current = false

    u.onstart = () => {
      startTimeRef.current = Date.now()
      setIsPlaying(true)
      setStatus('playing')
      // 500ms grace period: fall back to timer if onboundary never fires
      boundaryCheckTimeoutRef.current = window.setTimeout(() => {
        if (!boundaryFiredRef.current) startFallbackTimer()
      }, 500)
    }

    u.onboundary = (event) => {
      boundaryFiredRef.current = true
      clearInterval_() // onboundary is authoritative; stop timer fallback
      const pct = Math.min((event.charIndex / Math.max(description.length, 1)) * 100, 100)
      setProgress(pct)
    }

    u.onend = () => {
      clearInterval_()
      clearBoundaryCheck()
      setIsPlaying(false)
      setProgress(100)
      setStatus('done')
    }

    u.onerror = () => {
      clearInterval_()
      clearBoundaryCheck()
      setIsPlaying(false)
      setStatus('idle')
    }

    utteranceRef.current = u
    window.speechSynthesis.speak(u)
  }, [description, clearInterval_, clearBoundaryCheck, startFallbackTimer])

  const handlePlayPause = useCallback(() => {
    if (!('speechSynthesis' in window)) return
    if (isPlaying) {
      window.speechSynthesis.pause()
      clearInterval_()
      setIsPlaying(false)
      setStatus('paused')
    } else if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume()
      if (!boundaryFiredRef.current && durationEstimateRef.current > 0) {
        startTimeRef.current = Date.now() - (progress / 100) * durationEstimateRef.current
        startFallbackTimer()
      }
      setIsPlaying(true)
      setStatus('playing')
    } else {
      speak()
    }
  }, [isPlaying, progress, speak, clearInterval_, startFallbackTimer])

  const handleReplay = useCallback(() => {
    window.speechSynthesis.cancel()
    setProgress(0)
    setStatus('idle')
    speak()
  }, [speak])

  // Auto-play on mount
  useEffect(() => {
    if (!autoPlay) return
    const t = window.setTimeout(speak, autoPlayDelay)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel()
      clearInterval_()
      clearBoundaryCheck()
    }
  }, [clearInterval_, clearBoundaryCheck])

  return (
    <Box
      bg="lumene.night"
      borderRadius="lg"
      px={4}
      py={3}
      role="region"
      aria-label="Lecture vocale de la description"
    >
      <HStack spacing={3} align="center">
        <IconButton
          variant="lumeneAudio"
          aria-label={isPlaying ? 'Mettre en pause' : 'Lire la description'}
          aria-pressed={isPlaying}
          onClick={handlePlayPause}
          size="md"
          minW="44px"
          h="44px"
          icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
        />

        <VStack spacing={1} align="stretch" flex={1}>
          <Text
            fontSize="xs"
            color="lumene.copper"
            letterSpacing="4px"
            textTransform="uppercase"
            fontWeight="bold"
            aria-live="polite"
            aria-atomic="true"
          >
            {status === 'playing' && 'Lecture en cours'}
            {status === 'paused'  && 'En pause'}
            {status === 'done'    && 'Terminé'}
            {status === 'idle'    && 'Description'}
          </Text>

          <Box
            h="3px"
            bg="rgba(248,244,236,0.2)"
            borderRadius="2px"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress)}
            overflow="hidden"
          >
            <motion.div
              style={{ height: '100%', background: '#B08D5B', borderRadius: '2px' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: prefersReduced ? 0 : 0.1, ease: 'linear' }}
            />
          </Box>
        </VStack>

        <IconButton
          variant="lumeneAudioGhost"
          aria-label="Réécouter depuis le début"
          onClick={handleReplay}
          size="md"
          minW="40px"
          h="40px"
          icon={<ReplayIcon />}
        />
      </HStack>
    </Box>
  )
}

export default AudioBar
